import {
    backdropClasses,
    filledInputClasses,
    outlinedInputClasses,
    paperClasses,
    tableCellClasses
} from '@mui/material'
import { common } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'

export const createComponents = ({ palette }) => ({
    MuiAppBar: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(common.white, 0.62),
                color: palette.text.primary,
                backdropFilter: 'blur(14px)',
                borderBottom: `1px solid ${alpha(palette.neutral[900], 0.06)}`,
                boxShadow: `0px 10px 28px ${alpha(palette.neutral[900], 0.06)}`,
                backgroundImage: `linear-gradient(180deg, ${alpha(
                    common.white,
                    0.5
                )} 0%, ${alpha(common.white, 0.28)} 100%)`
            }
        }
    },
    MuiAccordion: {
        styleOverrides: {
            root: {
                border: '1px solid',
                borderColor: alpha(palette.neutral[900], 0.07),
                backgroundColor: alpha(common.white, 0.72),
                backdropFilter: 'blur(12px)',
                boxShadow: `0px 8px 24px ${alpha(palette.neutral[900], 0.05)}`,
                '&:before': {
                    display: 'none'
                }
            }
        }
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                backgroundColor: palette.neutral[100],
                color: palette.neutral[800]
            }
        }
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                [`&:not(.${backdropClasses.invisible})`]: {
                    backgroundColor: alpha(palette.neutral[900], 0.5),
                    backdropFilter: 'blur(6px)'
                }
            }
        }
    },
    MuiCard: {
        styleOverrides: {
            root: {
                border: `1px solid ${alpha(palette.neutral[900], 0.07)}`,
                backgroundColor: alpha(common.white, 0.72),
                backdropFilter: 'blur(16px)',
                backgroundImage: `linear-gradient(180deg, ${alpha(
                    common.white,
                    0.42
                )} 0%, ${alpha(common.white, 0.2)} 100%)`,
                transition:
                    'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease',
                [`&.${paperClasses.elevation1}`]: {
                    boxShadow:
                        '0px 12px 24px rgba(17, 25, 39, 0.06), 0px 1px 2px rgba(17, 25, 39, 0.05)'
                },
                '&:hover': {
                    borderColor: alpha(palette.primary.main, 0.14),
                    boxShadow:
                        '0px 18px 36px rgba(17, 25, 39, 0.08), 0px 4px 12px rgba(17, 25, 39, 0.05)'
                }
            }
        }
    },
    MuiButton: {
        styleOverrides: {
            root: {
                fontWeight: 600,
                letterSpacing: '-0.01em'
            },
            contained: {
                boxShadow: '0px 8px 18px rgba(17, 25, 39, 0.1)',
                '&:hover': {
                    boxShadow: '0px 12px 26px rgba(17, 25, 39, 0.14)'
                }
            },
            // Frost only, plus a lighter frost on hover. A border colour here would beat MUI's
            // per-colour styles, and an outlined error button would lose its red border.
            outlined: {
                backgroundColor: alpha(common.white, 0.45),
                backdropFilter: 'blur(8px)',
                '&:hover': {
                    backgroundColor: alpha(common.white, 0.62)
                }
            },
            text: {
                '&:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.06)
                }
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            icon: {
                color: palette.action.active
            },
            // Only the plain outlined chip is frosted. Root overrides beat MUI's colour classes, so
            // applying this to every chip turned a success chip into white text on near-white.
            root: ({ ownerState }) =>
                ownerState.color === 'default' &&
                ownerState.variant === 'outlined'
                    ? {
                          borderColor: palette.neutral[200],
                          backgroundColor: alpha(common.white, 0.65),
                          backdropFilter: 'blur(8px)'
                      }
                    : {}
        }
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                backgroundColor: alpha(common.white, 0.8),
                backdropFilter: 'blur(18px)',
                border: `1px solid ${alpha(palette.neutral[900], 0.06)}`,
                backgroundImage: `linear-gradient(180deg, ${alpha(
                    common.white,
                    0.56
                )} 0%, ${alpha(common.white, 0.24)} 100%)`,
                boxShadow:
                    '0px 24px 56px rgba(17, 25, 39, 0.14), 0px 8px 20px rgba(17, 25, 39, 0.08)'
            }
        }
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                borderColor: alpha(palette.neutral[900], 0.07)
            }
        }
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                borderRight: `1px solid ${alpha(palette.neutral[900], 0.06)}`,
                backgroundColor: alpha(common.white, 0.72),
                backdropFilter: 'blur(18px)',
                backgroundImage: `linear-gradient(180deg, ${alpha(
                    common.white,
                    0.54
                )} 0%, ${alpha(common.white, 0.22)} 100%)`
            }
        }
    },
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                background:
                    `radial-gradient(circle at 0% 0%, ${alpha(
                        palette.primary.main,
                        0.08
                    )} 0%, transparent 45%), ` +
                    `radial-gradient(circle at 100% 0%, ${alpha(
                        palette.neutral[400],
                        0.08
                    )} 0%, transparent 40%), ` +
                    palette.background.default
            },
            '#nprogress .bar': {
                backgroundColor: palette.primary.main
            },
            '.slick-dots li button': {
                '&:before': {
                    fontSize: 10,
                    color: palette.primary.main
                }
            },
            '.slick-dots li.slick-active button': {
                '&:before': {
                    color: palette.primary.main
                }
            }
        }
    },
    MuiFormLabel: {
        styleOverrides: {
            root: {
                backgroundColor: palette.background.paper,
                paddingRight: '4px',
                paddingLeft: '4px',
                color: palette.text.secondary,
                borderRadius: 4
            }
        }
    },
    MuiInputBase: {
        styleOverrides: {
            input: {
                '&::placeholder': {
                    color: palette.text.secondary
                }
            }
        }
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: 10,
                transition:
                    'background-color 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.05)
                },
                '&.Mui-selected': {
                    backgroundColor: alpha(palette.primary.main, 0.08),
                    boxShadow: `inset 0 0 0 1px ${alpha(
                        palette.primary.main,
                        0.08
                    )}`
                },
                '&.Mui-selected:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.11)
                }
            }
        }
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                backgroundColor: alpha(common.white, 0.76),
                backdropFilter: 'blur(16px)',
                border: `1px solid ${alpha(palette.neutral[900], 0.06)}`,
                boxShadow:
                    '0px 20px 44px rgba(17, 25, 39, 0.12), 0px 6px 16px rgba(17, 25, 39, 0.07)'
            },
            list: {
                paddingTop: 6,
                paddingBottom: 6
            }
        }
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                borderRadius: 10,
                margin: '2px 6px',
                '&:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.05)
                },
                '&.Mui-selected': {
                    backgroundColor: alpha(palette.primary.main, 0.09)
                },
                '&.Mui-selected:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.12)
                }
            }
        }
    },
    MuiFilledInput: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(common.white, 0.62),
                backdropFilter: 'blur(10px)',
                borderColor: alpha(palette.neutral[900], 0.08),
                '&:hover': {
                    backgroundColor: alpha(common.white, 0.78),
                    borderColor: alpha(palette.neutral[900], 0.14)
                },
                [`&.${filledInputClasses.disabled}`]: {
                    backgroundColor: 'transparent'
                },
                [`&.${filledInputClasses.focused}`]: {
                    backgroundColor: alpha(common.white, 0.86),
                    borderColor: palette.primary.main,
                    boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.12)}`
                },
                [`&.${filledInputClasses.error}`]: {
                    borderColor: palette.error.main,
                    boxShadow: `0 0 0 4px ${alpha(palette.error.main, 0.12)}`
                }
            }
        }
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                border: `1px solid ${alpha(palette.neutral[900], 0.1)}`,
                backgroundColor: alpha(common.white, 0.78),
                backdropFilter: 'blur(14px)',
                boxShadow: `inset 0 0 0 1px ${alpha(common.white, 0.28)}`
            }
        }
    },
    MuiPopover: {
        styleOverrides: {
            paper: {
                backgroundColor: alpha(common.white, 0.76),
                backdropFilter: 'blur(16px)',
                border: `1px solid ${alpha(palette.neutral[900], 0.06)}`
            }
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(common.white, 0.58),
                backdropFilter: 'blur(10px)',
                '&:hover': {
                    backgroundColor: alpha(common.white, 0.78),
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: alpha(palette.neutral[900], 0.15)
                    }
                },
                [`&.${outlinedInputClasses.focused}`]: {
                    backgroundColor: alpha(common.white, 0.86),
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: palette.primary.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.12)}`
                    }
                },
                [`&.${outlinedInputClasses.error}`]: {
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: palette.error.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.error.main, 0.12)}`
                    }
                }
            },
            notchedOutline: {
                borderColor: alpha(palette.neutral[900], 0.08)
            }
        }
    },
    MuiSwitch: {
        styleOverrides: {
            switchBase: {
                color: palette.neutral[500]
            },
            track: {
                backgroundColor: palette.neutral[400],
                opacity: 1
            }
        }
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                borderBottomColor: alpha(palette.neutral[900], 0.06)
            }
        }
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
                [`& .${tableCellClasses.root}`]: {
                    backgroundColor: alpha(common.white, 0.62),
                    backdropFilter: 'blur(10px)',
                    color: palette.neutral[700],
                    fontWeight: 600
                }
            }
        }
    },
    MuiTimelineConnector: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(palette.neutral[900], 0.1)
            }
        }
    },
    MuiTab: {
        styleOverrides: {
            root: {
                color: palette.text.secondary,
                '&.Mui-selected': {
                    color: palette.text.primary
                }
            }
        }
    },
    MuiTabs: {
        styleOverrides: {
            indicator: {
                height: 3,
                borderRadius: 999,
                backgroundColor: palette.primary.main,
                boxShadow: `0px 4px 12px ${alpha(palette.primary.main, 0.28)}`
            }
        }
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                backdropFilter: 'blur(12px)',
                background: alpha(palette.neutral[900], 0.84),
                border: `1px solid ${alpha(common.white, 0.08)}`
            }
        }
    }
})
